import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, HoldRow } from '../api';
import { eventEndpoints, holdEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * Every hold, in one place (D-258).
 *
 * Holds have only ever been visible one performance at a time, on that
 * performance's seat map. So "what are we holding, and what is about to
 * expire" — the question somebody asks on a Monday — meant opening each
 * performance in turn, and the Hold Report will not run without an event.
 *
 * **Ordered by what needs a decision**: open holds first, soonest to expire at
 * the top, and the ones with no expiry last, because those are waiting on a
 * person rather than a clock.
 *
 * Two actions, and the asymmetry is deliberate. **Release** is here, because it
 * is a decision about inventory and this is the inventory screen. **Selling**
 * out of a hold is not: it needs a customer, a price and a tender, which is the
 * window's job — so this links there rather than growing a second till.
 */

const expiryTone = (hold: HoldRow): 'bad' | 'warn' | 'neutral' => {
  if (!hold.auto_release_at) return 'neutral';
  const ms = new Date(hold.auto_release_at).getTime() - Date.now();
  if (ms <= 0) return 'bad';
  return ms < 48 * 3_600_000 ? 'warn' : 'neutral';
};

export default function HoldsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [eventId, setEventId] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const events = useQuery({ queryKey: ['events', 'all'], queryFn: () => eventEndpoints.list() });
  const holds = useQuery({
    queryKey: ['holds', eventId, includeClosed],
    queryFn: () => holdEndpoints.list({ ...(eventId ? { event_id: eventId } : {}), ...(includeClosed ? { include_closed: true } : {}) }),
  });

  const release = useMutation({
    mutationFn: (hold: HoldRow) => holdEndpoints.release(hold.event_id, hold.performance_id, hold.id),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['holds'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  if (!can('inventory.view')) return <NotPermitted permission="inventory.view" />;
  if (holds.error) return <ErrorNote>{(holds.error as ApiError).message}</ErrorNote>;
  const rows = holds.data?.holds ?? [];
  const openUnits = rows.filter((h) => !h.closed_at).reduce((sum, h) => sum + h.units, 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-ink-900">Holds</h1>
        <p className="mt-1 text-sm text-ink-600">
          Seats kept back — for the artist, the promoter, the house. {openUnits} seat{openUnits === 1 ? '' : 's'} held right now. A hold with a release time lets go of its seats on
          its own; one without waits for somebody here.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={`${INPUT} w-72`} aria-label="Event">
          <option value="">Every event</option>
          {(events.data?.events ?? []).map((event) => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-ink-600">
          <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} className="h-4 w-4 accent-brand" />
          Show closed
        </label>
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel title={`${rows.length} hold${rows.length === 1 ? '' : 's'}`}>
        <Table headers={['Hold', 'Show', 'Seats', 'Releases', '']}>
          {rows.length === 0 ? (
            <EmptyRow columns={5}>Nothing held.</EmptyRow>
          ) : (
            rows.map((hold) => (
              <tr key={hold.id} className={`border-b border-line last:border-0 ${hold.closed_at ? 'text-ink-400' : ''}`}>
                <td className="px-5 py-3">
                  <span className="font-medium text-ink-900">{hold.name}</span>
                  <div className="text-xs text-ink-500">
                    {hold.hold_class_name}
                    {hold.owner_name ? ` · ${hold.owner_name}` : ''}
                    {hold.notes ? ` · ${hold.notes}` : ''}
                  </div>
                </td>
                <td className="px-5 py-3 text-sm">
                  <Link to={`/events/${hold.event_id}`} className="hover:underline">
                    {hold.event_title}
                  </Link>
                  <div className="text-xs text-ink-500">
                    {formatWhen(hold.starts_at)} · {hold.venue_name}
                  </div>
                </td>
                <td className="tabular px-5 py-3">{hold.units}</td>
                <td className="px-5 py-3 text-sm">
                  {hold.closed_at ? (
                    <Badge tone="neutral">closed</Badge>
                  ) : hold.auto_release_at ? (
                    <Badge tone={expiryTone(hold)}>{formatWhen(hold.auto_release_at)}</Badge>
                  ) : (
                    <span className="text-ink-400">when somebody says</span>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  {hold.closed_at || hold.units === 0 ? null : (
                    <div className="flex justify-end gap-3 text-xs">
                      {/* Selling needs a customer, a price and a tender: that is
                          the window's job, so this points there rather than
                          growing a second till here. */}
                      {/* Straight to this performance at the window, where held seats show in amber (D-330). */}
                      {hold.sell_url ? (
                        <a href={hold.sell_url} target="_blank" rel="noreferrer" className="text-brand underline">
                          Sell at the window
                        </a>
                      ) : null}
                      <Link to={`/events/${hold.event_id}`} className="text-brand underline">
                        Seat map
                      </Link>
                      {can('inventory.hold.release') ? (
                        <button
                          type="button"
                          className="text-ink-500 underline"
                          disabled={release.isPending}
                          onClick={() => {
                            if (window.confirm(`Release all ${hold.units} seat${hold.units === 1 ? '' : 's'} from "${hold.name}"? They go back on sale immediately.`)) {
                              release.mutate(hold);
                            }
                          }}
                        >
                          Release all
                        </button>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
